'use client';

import { useState, Fragment, useEffect } from 'react';
import { useExtensionAuth } from 'src/hooks/useExtensionAuth';
import { XMarkIcon, CursorArrowRaysIcon, RocketLaunchIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { CHROME_EXTENSION } from 'src/constants/extension';

export default function ExtensionBanner() {
  const { extensionStatus } = useExtensionAuth();
  const [showDetails, setShowDetails] = useState(false);

  // Only show on Chrome/Chromium browsers that have the extension installed
  const isChromiumBased = typeof window !== 'undefined' && 
    typeof window.chrome !== 'undefined';

  // Don't show banner while checking or if there's an error or browser doesn't support extensions
  if (!isChromiumBased || extensionStatus.isChecking || extensionStatus.error || !extensionStatus.isInstalled) {
    return null;
  }

  // Subtle indicator - small badge/pill that shows extension is connected
  return (
    <>
      <button
        onClick={() => setShowDetails(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full text-xs hover:bg-green-100 hover:border-green-300 transition-colors cursor-pointer"
        title="Click for extension details"
      >
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        <span className="text-green-700 font-medium">Extension Active</span>
      </button>

      {/* Extension Details Modal */}
      <Transition appear show={showDetails} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowDetails(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-3xl bg-gradient-to-br from-white to-gray-50 text-left align-middle shadow-2xl transition-all border border-gray-100">
                  {/* Header with gradient background */}
                  <div className="relative bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-5">
                    <Dialog.Title className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                          <RocketLaunchIcon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">
                            Extension Connected!
                          </h3>
                          <p className="text-green-100 text-sm">Ready to verify facts anywhere</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowDetails(false)}
                        className="text-white/70 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </Dialog.Title>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Status Card */}
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-50 to-emerald-50 p-4 border border-green-200">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                          <ShieldCheckIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-green-800">Synced & Secure</p>
                          <p className="text-sm text-green-600">Extension authenticated with your account</p>
                        </div>
                      </div>
                      {/* Animated background decoration */}
                      <div className="absolute -right-4 -top-4 w-16 h-16 bg-green-200/30 rounded-full"></div>
                      <div className="absolute -right-2 -bottom-2 w-12 h-12 bg-emerald-200/40 rounded-full"></div>
                    </div>

                    {/* How to Use - Visual Steps */}
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <CursorArrowRaysIcon className="h-5 w-5 text-blue-500" />
                        How to verify facts:
                      </h4>
                      <div className="space-y-3">
                        {[
                          { step: "1", action: "Select text on any webpage", icon: "🎯", color: "blue" },
                          { step: "2", action: "Right-click → 'Verify with Mira'", icon: "🖱️", color: "purple" },
                          { step: "3", action: "Get instant AI consensus results", icon: "⚡", color: "green" }
                        ].map((item) => (
                          <div key={item.step} className="flex items-center gap-4 p-3 rounded-xl bg-white border border-gray-100 hover:shadow-md transition-shadow">
                            <div className="flex-shrink-0">
                              <div className={`w-8 h-8 rounded-full bg-${item.color}-100 flex items-center justify-center text-lg`}>
                                {item.icon}
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-bold px-2 py-1 rounded-full bg-${item.color}-100 text-${item.color}-800`}>
                                  STEP {item.step}
                                </span>
                                <span className="text-sm font-medium text-gray-900">{item.action}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Pro Tips */}
                    <div className="rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 p-4 border border-amber-200">
                      <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                        💡 Pro Tips
                      </h4>
                      <ul className="space-y-1 text-sm text-amber-700">
                        <li>• Perfect for news articles, social media posts, and research</li>
                        <li>• Works instantly on any website - no page reload needed</li>
                        <li>• AI consensus from multiple models ensures accuracy</li>
                      </ul>
                    </div>

                    {/* Call to Action */}
                    <div className="text-center pt-2">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full text-sm font-medium shadow-lg">
                        <span>Ready to verify facts!</span>
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

export function ExtensionInstallPrompt() {
  const { extensionStatus } = useExtensionAuth();
  const [isDismissed, setIsDismissed] = useState(() => {
    // Check localStorage for dismissed state
    if (typeof window !== 'undefined') {
      return localStorage.getItem('extensionBannerDismissed') === 'true';
    }
    return false;
  });

  // Extension status changes tracked via state

  // Reset dismissed state in development with keyboard shortcut
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const handleKeyPress = (e: KeyboardEvent) => {
        // Press Ctrl+Shift+E to reset extension banner
        if (e.ctrlKey && e.shiftKey && e.key === 'E') {
          localStorage.removeItem('extensionBannerDismissed');
          setIsDismissed(false);
          // Extension banner reset
        }
      };
      
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('extensionBannerDismissed', 'true');
      } catch (error) {
        console.error('Failed to save dismissal state:', error);
      }
    }
  };

  // Only show on Chrome/Chromium browsers (they have window.chrome object)
  const isChromiumBased = typeof window !== 'undefined' && 
    typeof window.chrome !== 'undefined';

  // Show install prompt if extension is not installed
  if (!isChromiumBased || extensionStatus.isChecking || extensionStatus.isInstalled || isDismissed) {
    return null;
  }

  return (
    <div className="relative bg-gray-50 border border-gray-200 rounded-lg mb-6 overflow-hidden">
      <div className="p-4 sm:p-6">
        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Dismiss banner"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
        
        <div className="flex items-center gap-4 sm:gap-6">
          {/* Chrome icon */}
          <div className="hidden sm:flex items-center justify-center w-16 h-16 bg-white rounded-lg shadow-sm border border-gray-200 flex-shrink-0">
            <svg className="w-10 h-10" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="#4285F4"/>
              <circle cx="12" cy="12" r="4" fill="#0F9D58"/>
              <path d="M12 2L12 12L22 12A10 10 0 0 0 12 2Z" fill="#EA4335"/>
              <path d="M12 12L12 22A10 10 0 0 0 22 12L12 12Z" fill="#FBBC04"/>
            </svg>
          </div>
          
          {/* Content */}
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Enhance your browsing with the Fact Verifier extension
            </h3>
            <p className="text-gray-600 text-sm mb-3">
              Verify facts instantly on any webpage with a simple right-click. Get AI-powered consensus results without leaving your current tab.
            </p>
            
            <div className="flex flex-wrap items-center gap-3">
              <a
                href={CHROME_EXTENSION.STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="white" fillOpacity="0.3"/>
                  <circle cx="12" cy="12" r="4" fill="white" fillOpacity="0.5"/>
                  <path d="M12 2L12 12L22 12A10 10 0 0 0 12 2Z" fill="white" fillOpacity="0.7"/>
                  <path d="M12 12L12 22A10 10 0 0 0 22 12L12 12Z" fill="white" fillOpacity="0.9"/>
                </svg>
                <span>Add to Chrome</span>
              </a>
              
              {/* Features */}
              <div className="flex items-center gap-3 text-gray-500 text-xs">
                <span className="flex items-center gap-1">
                  <ShieldCheckIcon className="h-3.5 w-3.5" />
                  Secure
                </span>
                <span className="flex items-center gap-1">
                  <RocketLaunchIcon className="h-3.5 w-3.5" />
                  Fast
                </span>
                <span className="flex items-center gap-1">
                  <CursorArrowRaysIcon className="h-3.5 w-3.5" />
                  Easy
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}